import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Check, ErrorOutline } from '@mui/icons-material';
import {
  capitalize,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { TableComponent } from 'commons/components/Table';
import { BinType } from 'commons/enums';
import * as colors from 'commons/styles/colors';
import { PartialPickingTask, PickingBySKUsDetails } from 'commons/types';
import {
  assignBasket,
  getPickTaskBySKU,
  postRemoveBatch,
  getPickItemBinRecommendationInfo,
  postBarcodeVerification,
} from 'redux-stores/actions';
import { selectUser } from 'redux-stores/features/authSlice';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';
import { PICK_TASK_ROUTES } from 'routes/constants';

import { AssignBasketModal } from '../commons/components/assign-basket-modal';
import { onRequestStocks } from '../commons/components/utils';

import PickingProcess from './components/PickProcess.component';
import { IPickProcessState } from './interfaces';
import S from './PickItemPage.style';
import { config } from './pickItemTable.config';

const PickItemPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {
    state: { pickIdx },
  } = useLocation();
  const user = useSelector(selectUser);
  const {
    tasksBySKUs,
    skuList,
    skuLowStockTriggered,
    invalidSKUs,
    binRecommendationInfo,
  } = useSelector((state: RootReducerInterface) => state.pickingTask);

  /** ******** STATES ********* */
  const [pickList, setPickList] = useState(tasksBySKUs[pickIdx]);
  const [controlIdx, setControlIdx] = useState(pickIdx);
  const [isAllBasketVerified, setIsAllBasketVerified] = useState(false);
  const [isBarcodeVerified, setIsBarcodeVerified] = useState<boolean>(
    !pickList?.barcode || pickList?.is_barcode_scanned || false,
  );
  const [barcodeScanError, setBarcodeScanError] = useState<boolean>(false);
  const [barcodeVerifiedOnProcess, setBarcodeVerifiedOnProcess] =
    useState<boolean>(false);
  const [barcodeValue, setBarcodeValue] = useState<string | null>(
    pickList?.is_barcode_scanned ? pickList?.barcode || null : null,
  );
  const [recommendationBinType, setRecommendationBinType] =
    useState<BinType | null>(BinType.RETAIL);

  const [openAssignBasketModal, setOpenAssignBasketModal] =
    useState<boolean>(false);
  const [taskModal, setTaskModal] = useState<PartialPickingTask | null>(null);
  const onModalClose = (): void => setOpenAssignBasketModal(false);
  const onModalOpen = (detail: PickingBySKUsDetails): void => {
    setTaskModal({ baskets: detail.baskets, po_number: detail.po_number });
    setOpenAssignBasketModal(true);
  };

  const [pickProcessState, setPickProcessState] = useState<
    Map<string, IPickProcessState>
  >(new Map());
  const [pickedProgress, setPickedProgress] = useState(0);
  const [colorProgress, setColorProgress] = useState(colors.PRIMARY_RED);
  const [isStockOut, setIsStockOut] = useState<boolean>(false);

  /** ******** FUNCTIONS ********* */
  const debounceVerifyBarcodeAPI = useDebouncedCallback(
    (input: string) => {
      setBarcodeVerifiedOnProcess(true);
      postBarcodeVerification(
        {
          sku_code: pickList.sku_code,
          barcode: input || '',
          po_numbers: pickList.details.map((task) => task.po_number),
        },
        dispatch,
      ).then((result) => {
        if (result) {
          setIsBarcodeVerified(true);
          setBarcodeVerifiedOnProcess(false);
        } else {
          setBarcodeScanError(true);
          setIsBarcodeVerified(false);
          setBarcodeVerifiedOnProcess(false);
        }
      });
    },
    500,
    {
      maxWait: 2000,
    },
  );
  const getSourceBinTableData = (value: BinType): void => {
    dispatch(
      getPickItemBinRecommendationInfo({
        skuCode: pickList.sku_code,
        binType: value,
      }),
    );
    setRecommendationBinType(value);
  };

  const verifyBarcode = (input: string) => {
    if (!pickList.barcode) return;

    const trimmedInput = input.replace(/[^0-9]*/g, '');
    setBarcodeValue(trimmedInput || null);
    setBarcodeScanError(false);
    debounceVerifyBarcodeAPI(trimmedInput);
  };

  const defaultPickingInfo = (
    basket: number,
    poNumber: string,
  ): IPickProcessState => ({
    poNumber,
    isOpen: true,
    picked: [
      {
        basket,
        bin: '',
        inventory_number: '',
        expiry_date: '',
        quantity: 0,
        is_verified: false,
      },
    ],
  });

  const isFirstPage = useMemo(() => {
    return pickIdx <= 0;
  }, [pickIdx]);
  const isLastPage = useMemo(() => {
    return pickIdx >= tasksBySKUs.length - 1;
  }, [pickIdx, tasksBySKUs.length]);

  const goToPrevSKUHandler = (): void => {
    if (isFirstPage) {
      navigate(PICK_TASK_ROUTES.LIST);
      return;
    }
    const newIdx = pickIdx - 1;
    navigate(PICK_TASK_ROUTES.ITEM, { state: { pickIdx: newIdx } });
  };

  const goToNextSKUHandler = (): void => {
    if (isLastPage) {
      navigate(PICK_TASK_ROUTES.LIST);
      return;
    }
    const newIdx = pickIdx + 1;
    navigate(PICK_TASK_ROUTES.ITEM, { state: { pickIdx: newIdx } });
  };

  const onAssignBasketConfirm = (task: PartialPickingTask): void => {
    const assignBasketDispatch = async (): Promise<void> => {
      /* sequential: assign basket, and then after that refresh the pick task with the updated basket */
      await dispatch(assignBasket({ task }));
      /* FIXME temporary fix for intermitten issue: add wait interval for better chance it get the updated basket */
      setTimeout(async () => {
        await dispatch(getPickTaskBySKU());
      }, 500);
    };

    assignBasketDispatch();
    setOpenAssignBasketModal(false);
  };

  /** ******** USE EFFECTS ********* */
  useEffect(() => {
    if (controlIdx !== pickIdx) {
      dispatch(getPickTaskBySKU());
      setControlIdx(pickIdx);
    } else {
      /* there's a chance for taskBySKUs be reordered, need to find the real current sku task */
      const currentTask = tasksBySKUs.find(
        (task) => task.sku_code === skuList[pickIdx],
      );
      /* fallback to tasksBySKUs index if current sku not found in skuList */
      const newPicklist = currentTask || tasksBySKUs[pickIdx];
      setPickList(newPicklist);
      setIsBarcodeVerified(
        !newPicklist?.barcode || newPicklist?.is_barcode_scanned || false,
      );
      setBarcodeScanError(false);
      setBarcodeValue(
        newPicklist?.is_barcode_scanned ? newPicklist?.barcode || null : null,
      );
    }
  }, [controlIdx, dispatch, pickIdx, skuList, tasksBySKUs]);

  useEffect(() => {
    // set default
    if (pickList) {
      dispatch(
        getPickItemBinRecommendationInfo({
          skuCode: pickList.sku_code,
          binType: BinType.RETAIL,
        }),
      );
    }
  }, [dispatch, pickList]);

  useEffect(() => {
    if (pickList && recommendationBinType === BinType.RETAIL) {
      const recommendationTotalQty = binRecommendationInfo.reduce(
        (total, rec) => total + rec.quantity,
        0,
      );
      const pickActualTotalQty = pickList.details.reduce(
        (sum, detail) =>
          sum +
          detail.picked_actual.reduce(
            (sum2, actual) => sum2 + actual.quantity,
            0,
          ),
        0,
      );
      setIsStockOut(
        !skuLowStockTriggered[pickList.sku_code] &&
          recommendationTotalQty < pickList.total_quantity - pickActualTotalQty,
      );
    }
  }, [binRecommendationInfo]);

  useEffect(() => {
    let quantity = 0;
    let _isAllBasketVerified = true;
    pickProcessState.forEach((pick) => {
      pick.picked.forEach((picked) => {
        quantity += picked.quantity;
        /* 
          check if all basket is verified. 
          if quantity is 0, treat it as verified 
          as it means the basket is not yet filled and no item is picked, 
          so no need to verify the basket yet 
        */
        _isAllBasketVerified =
          _isAllBasketVerified && (picked.is_verified || picked.quantity === 0);
      });
    });
    setPickedProgress(quantity);
    const color =
      quantity === 0
        ? colors.PRIMARY_RED
        : quantity === pickList?.total_quantity
        ? colors.PRIMARY_GREEN
        : colors.PRIMARY_YELLOW;
    setColorProgress(color);
    setIsAllBasketVerified(_isAllBasketVerified);
  }, [pickList?.total_quantity, pickProcessState]);

  useEffect(() => {
    const initPickProcessState = (): Map<string, IPickProcessState> => {
      const result = new Map();

      pickList?.details.forEach((detail) =>
        detail.baskets.forEach((basket) => {
          const sku = pickList.sku_code;
          const key = `${detail.po_number}-${sku}-${basket}`;
          // check existing data. if no data picked yet, use defaultPickingInfo
          const existingPickedData = detail.picked_actual.filter(
            (picked) => picked.basket === basket,
          );
          if (existingPickedData.length > 0) {
            result.set(key, {
              isOpen: true,
              picked: existingPickedData,
              quantity: detail.quantity,
              poNumber: detail.po_number,
            });
          } else {
            result.set(key, defaultPickingInfo(basket, detail.po_number));
          }
        }),
      );

      return result;
    };
    setPickProcessState(initPickProcessState());
  }, [pickList?.details, pickList?.sku_code]);

  useEffect(() => {
    if (pickList) {
      invalidSKUs[pickList.sku_code]?.invalidBaskets?.forEach(
        (invalidBasket) => {
          postRemoveBatch({
            binNumber: invalidBasket.bin,
            inventoryNumber: invalidBasket.inventory_number,
            skuCode: pickList.sku_code,
            poNumber: invalidBasket.po_number,
            basket: invalidBasket.basket,
          });
        },
      );
    }
  }, [dispatch, invalidSKUs, pickList]);

  // early return process if pickList is null
  // to handle case page being refreshed
  if (!pickList) {
    navigate(PICK_TASK_ROUTES.LIST);
    return <div />;
  }

  // early return if no taskBySKU found in state
  if (tasksBySKUs.length === 0) {
    navigate(PICK_TASK_ROUTES.LIST);
    return <div />;
  }
  return (
    <div>
      {/* Content Section */}
      <S.ContentWrapper>
        <S.SubtitleWrapper container>
          <S.Subtitle item xs={12} data-testid="TextItemSKU">
            {pickList?.sku_code}
          </S.Subtitle>
          <S.Subtitle item xs={12} data-testid="TextItemProduct">
            {pickList?.sku_name}
          </S.Subtitle>
          <S.UOMSubtitle item xs={12} data-testid="TextItemUom">
            {pickList?.uom}
          </S.UOMSubtitle>
        </S.SubtitleWrapper>
        <S.HeaderContentWrapper container>
          <Grid item xs={6}>
            <S.ProgressText data-testid="TextItemOrderQty">
              TOTAL ORDER QTY: {pickList?.total_quantity}
            </S.ProgressText>
          </Grid>
          <Grid item xs={6} style={{ textAlign: 'right' }}>
            <S.ProgressText>
              ORDER PICKED:
              <S.ProgressSpan
                color={colorProgress}
                data-testid="TextItemOrderPicked"
              >
                {pickedProgress}/{pickList?.total_quantity}
              </S.ProgressSpan>
            </S.ProgressText>
          </Grid>
        </S.HeaderContentWrapper>
        <S.HeaderTableWrapper container>
          <Grid item xs={6} sm={3} md={2}>
            <S.SubHeaderText>Recommend Bins</S.SubHeaderText>
          </Grid>
          <Grid item xs={6} sm={9} md={10}>
            <FormControl
              sx={{
                '.MuiInputLabel-root.Mui-focused': {
                  display: 'none', // Hide label on hover
                },
              }}
            >
              {!recommendationBinType && (
                <InputLabel sx={{ transition: 'none', fontSize: 14 }}>
                  Select Bin Type
                </InputLabel>
              )}
              <S.SelectBinType
                value={recommendationBinType || ''}
                onChange={(e: SelectChangeEvent) =>
                  getSourceBinTableData(e.target.value as BinType)
                }
                inputProps={{
                  'data-testid': 'SelectSTTaskSourceBinType',
                }}
              >
                {Object.values(BinType).map((_binType) => (
                  <MenuItem value={_binType}>
                    {capitalize(_binType.replace('_', ' '))}
                  </MenuItem>
                ))}
              </S.SelectBinType>
            </FormControl>
          </Grid>
          <TableComponent
            data={binRecommendationInfo}
            config={config}
            additionalTableConfig={{ bodyFontSize: '12px' }}
          />
        </S.HeaderTableWrapper>
        {isStockOut && (
          <S.HeaderReplenishContentWrapper>
            <S.HeaderReplenishGridContainer container>
              <S.HeaderReplenishGrid item xs={6}>
                Out of stock.
              </S.HeaderReplenishGrid>
              <S.HeaderReplenishGrid style={{ alignItems: 'end' }} item xs={6}>
                <S.BlueButtonTextNoBG
                  style={{
                    padding: 0,
                    float: 'right',
                  }}
                  onClick={() =>
                    onRequestStocks(pickList, user.warehouseId, dispatch)
                  }
                >
                  REQUEST STOCKS
                </S.BlueButtonTextNoBG>
              </S.HeaderReplenishGrid>
            </S.HeaderReplenishGridContainer>
          </S.HeaderReplenishContentWrapper>
        )}

        {/* Barcode Scan Section */}
        {/* SOFT REMOVE: will be totally removed if featured really not used anymore */}
        {/* <S.HeaderContentWrapper
          container
          display={{ xs: pickList.barcode ? 'inline-flex' : 'none' }}
        >
          <Grid item xs={6} md={3}>
            <S.ProgressText data-testid="ProductBarcode">
              PRODUCT BARCODE:
            </S.ProgressText>
          </Grid>
          <Grid item xs={6} md={9} style={{ marginTop: '5px' }}>
            <TextField
              variant="outlined"
              placeholder=""
              type="tel"
              disabled={isBarcodeVerified || barcodeVerifiedOnProcess}
              value={barcodeValue || ''}
              onChange={(event) => verifyBarcode(event.target.value)}
              id="field-product-barcode"
              error={barcodeScanError}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton>
                      {isBarcodeVerified ? (
                        <Check style={{ color: colors.PRIMARY_GRAY }} />
                      ) : barcodeScanError ? (
                        <ErrorOutline style={{ color: colors.PRIMARY_RED }} />
                      ) : (
                        ''
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { paddingRight: 0 },
              }}
            />
          </Grid>
        </S.HeaderContentWrapper> */}

        {/* Content Section */}
        <PickingProcess
          pickList={pickList}
          skuLowStockTriggered={skuLowStockTriggered}
          setIsAllBasketVerified={setIsAllBasketVerified}
          onModalAddBasketOpen={onModalOpen}
          pickProcessState={pickProcessState}
          setPickProcessState={setPickProcessState}
          // barcodeVerified={isBarcodeVerified}
        />
        <S.BlankWhiteSpace />
      </S.ContentWrapper>

      {/* Footer Section */}
      <S.FooterWrapper>
        <Grid container>
          <Grid item xs={6}>
            <S.SecondaryButton
              onClick={goToPrevSKUHandler}
              disabled={!isAllBasketVerified}
              data-testid="BtnItemPrev"
            >
              {isFirstPage ? 'BACK TO PICK LIST' : 'PREV SKU'}
            </S.SecondaryButton>
          </Grid>
          <Grid item xs={6}>
            <S.PrimaryButton
              onClick={goToNextSKUHandler}
              disabled={!isAllBasketVerified}
              data-testid="BtnNext"
            >
              {isLastPage ? 'DONE' : 'NEXT SKU'}{' '}
              {isAllBasketVerified ? '✔' : '✘'}
            </S.PrimaryButton>
          </Grid>
        </Grid>
      </S.FooterWrapper>

      {/* Modal */}
      {openAssignBasketModal && (
        <AssignBasketModal
          task={taskModal}
          onClose={onModalClose}
          openModal={openAssignBasketModal}
          onConfirm={onAssignBasketConfirm}
          removeDisabled
          disableExisting
        />
      )}
    </div>
  );
};

export default PickItemPage;
